import clsx from 'clsx';
import useDraggableScroll from 'use-draggable-scroll';
import {
  useRef,
  useState,
  Children,
  useEffect,
  cloneElement,
  isValidElement,
  type RefObject,
  type ReactNode,
  type ReactElement,
  type CSSProperties,
} from 'react';

import { Tab, type TabProps } from '../tab';

import { toRem } from '../../utils/ui';

import './tabs.scss';

export const Tabs = (props: TabsProps) => {
  const { value, onChange, indicatorClassName, children, className, size = 'default', appearance = 'default' } = props;

  const [indicator, setIndicator] = useState<Indicator>({
    left: 0,
    width: 0,
  });

  const showIndicator = appearance !== 'elevated';

  const containerRef = useRef<HTMLUListElement>(null);

  const { onMouseDown } = useDraggableScroll(containerRef as RefObject<HTMLElement>, { direction: 'horizontal' });

  const tabChildren = Children.toArray(children).filter(
    (child): child is ReactElement<TabProps> => isValidElement(child) && child.type === Tab
  );

  const getActiveIndex = () => {
    return tabChildren.findIndex((tab) => tab.props.id === value);
  };

  const moveIndicator = (index: number) => {
    if (containerRef.current) {
      const currentItem = containerRef.current.children[index] as HTMLLIElement;

      if (currentItem) {
        requestAnimationFrame(() => {
          const label = currentItem.querySelector('.label') as HTMLElement | null;

          if (label && containerRef.current) {
            const labelRect = label.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();

            setIndicator({
              left: labelRect.left - containerRect.left,
              width: labelRect.width,
            });
          }
        });
      }
    }
  };

  useEffect(() => {
    containerRef.current?.scrollTo?.({ left: 0, behavior: 'instant' });
  }, [containerRef]);

  useEffect(() => {
    if (showIndicator) {
      const activeIndex = getActiveIndex();
      moveIndicator(activeIndex);
    }
  }, [value, tabChildren.length]);

  return (
    <ul className={clsx('tabs-holder', `a-${appearance}`, `s-${size}`, className)} onMouseDown={onMouseDown} ref={containerRef}>
      {tabChildren.map((child) => {
        return cloneElement(child, {
          ...child.props,
          active: value === child.props.id,
          onTabClick: () => {
            onChange(child.props.id);
          },
        });
      })}
      {showIndicator && !!tabChildren.length && (
        <div
          style={
            {
              width: toRem(indicator.width),
              '--x': toRem(indicator.left),
            } as CSSProperties
          }
          className={clsx('tab-indicator', indicatorClassName)}
        />
      )}
    </ul>
  );
};

interface TabsProps {
  value: string;
  className?: string;
  children: ReactNode;
  indicatorClassName?: string;
  size?: 'default' | 'small';
  onChange: (value: string) => void;
  appearance?: 'default' | 'elevated' | 'simple';
}

type Indicator = {
  left: number;
  width: number;
};
